// RUT chileno — única fuente de verdad de la validación módulo 11 en el front.
//
// Por qué existe: antes había dos implementaciones paralelas (fiscalValidation.js y
// superadmin/lib/format.js) y varios formularios que solo comprobaban que el campo no
// estuviera vacío. Un RUT con el dígito verificador equivocado pasa esa comprobación y
// entra a producción — y (RUT emisor, folio) es la llave con la que el backend detecta
// documentos repetidos, así que un RUT errado rompe ese control.
//
// El backend revalida siempre; esto es feedback inmediato para el operador.

/** Normaliza a cuerpo+dv sin puntos, guiones ni espacios, en mayúsculas. */
export function cleanRut(value) {
  return String(value ?? "").replace(/[^0-9kK]/g, "").toUpperCase();
}

/** Dígito verificador (módulo 11) del cuerpo numérico de un RUT. */
export function rutDv(cuerpo) {
  let suma = 0;
  let mult = 2;
  for (const c of String(cuerpo).split("").reverse()) {
    suma += Number(c) * mult;
    mult = mult === 7 ? 2 : mult + 1;
  }
  const resto = 11 - (suma % 11);
  return resto === 11 ? "0" : resto === 10 ? "K" : String(resto);
}

/**
 * Valida un RUT chileno (con o sin puntos/guión).
 * Delega en validateRut para que exista una sola regla en todo el front.
 * @returns {boolean}
 */
export function isValidRut(value) {
  return validateRut(value).ok;
}

/** Agrupa un cuerpo numérico con puntos de mil: 12345678 -> 12.345.678 */
function groupDigits(cuerpo) {
  return String(cuerpo).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/**
 * Formatea un RUT tomando el último carácter como dígito verificador, que es lo que
 * necesita el tipeo progresivo: 12345678k -> 12.345.678-K.
 */
export function formatRut(value) {
  const limpio = cleanRut(value);
  if (limpio.length < 2) return limpio;
  return `${groupDigits(limpio.slice(0, -1))}-${limpio.slice(-1)}`;
}

/** Estados posibles: "empty" | "incomplete" | "badDv" | "ok". */
export const RUT_STATE = {
  EMPTY: "empty",
  INCOMPLETE: "incomplete",
  BAD_DV: "badDv",
  OK: "ok",
};

// Un RUT chileno tiene 2..9 caracteres limpios (cuerpo + DV); cuerpos de 6 dígitos o menos
// no existen en la práctica. Por debajo de este largo el valor todavía se está escribiendo
// y no corresponde acusar "DV equivocado".
const MIN_LENGTH = 8;

/**
 * Valida y explica. Es el punto de entrada que deben usar los formularios:
 * devuelve el estado y, cuando el DV está mal, el DV que corresponde — así el
 * mensaje puede decirle al operador exactamente qué corregir.
 *
 * @param {string} value
 * @returns {{state: string, ok: boolean, expectedDv: string|null, formatted: string}}
 */
export function validateRut(value) {
  const limpio = cleanRut(value);
  const formatted = limpio.length > 1 ? formatRut(limpio) : limpio;
  if (!limpio) return { state: RUT_STATE.EMPTY, ok: false, expectedDv: null, formatted: "" };
  // Demasiado corto, con caracteres imposibles o fuera de rango: se sigue escribiendo.
  if (limpio.length < MIN_LENGTH || limpio.length > 9 || !/^[0-9]+[0-9K]$/.test(limpio)) {
    return { state: RUT_STATE.INCOMPLETE, ok: false, expectedDv: null, formatted };
  }
  const cuerpo = limpio.slice(0, -1);
  const dv = limpio.slice(-1);
  const esperado = rutDv(cuerpo);
  if (esperado !== dv) {
    return {
      state: RUT_STATE.BAD_DV,
      ok: false,
      expectedDv: esperado,
      formatted: `${groupDigits(cuerpo)}-${esperado}`,
    };
  }
  return { state: RUT_STATE.OK, ok: true, expectedDv: esperado, formatted };
}

/**
 * Clave i18n del mensaje de error (clave = español natural, ver src/i18n).
 * @param {{state: string, expectedDv?: string|null}} result
 */
export function rutErrorKey(result) {
  if (!result || result.state === RUT_STATE.OK || result.state === RUT_STATE.EMPTY) return null;
  if (result.state === RUT_STATE.BAD_DV) return "RUT inválido: el dígito verificador debería ser {{dv}}";
  return "RUT incompleto: revisa los dígitos";
}
